import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { watch } from '../../internal/watch.js';
import HarmonyElement from '../../base-components/base.js';
import componentStyles from '../../internal/styles/component.styles.js';
import { Component } from '../../utilities/decorators.js';
import { requestIcon } from './request.js';
import styles from './icon.styles.js';
import { IconNames } from './icon-names.js';

const parser = new DOMParser();
export type { IconNames } from './icon-names.js';
export interface IconErrorEvent {
  status: number;
}

/**
 *
 * Icons are symbols that can be used to represent various options within an application.
 *
 * @tag he-icon
 * @since 1.1
 * @status stable
 * @figma https://www.figma.com/file/UvgzWQM5R18Lrs4VHs2UPd/Partner-Center-extended-toolkit?type=design&node-id=91%3A21375&mode=design&t=FrLbCdXM439ktBGm-1
 *
 * @event he-ready - Emitted when the component has completed its initial render.
 * @event he-load - Emitted when the icon has loaded.
 * @event {IconErrorEvent} he-error - Emitted when the icon failed to load.
 *
 * @csspart base - The base wrapper around the icon.
 */
@Component('icon')
export class Icon extends HarmonyElement {
  static styles = [componentStyles, styles];
  static reactEvents = {
    onHeReady: new CustomEvent('he-ready'),
    onHeLoad: new CustomEvent('he-load'),
    onHeError: new CustomEvent<IconErrorEvent>('he-error'),
  };

  @state()
  private svg = '';

  /** The name of the icon to draw. */
  @property({ reflect: true }) name?: IconNames;

  /** A description to use for accessibility. If omitted, `aria-hidden="true"` will be applied instead. */
  @property() label?: string;

  /** A string that points to an SVG. This is an escape hatch for loading icons not from the basePath. */
  @property() url?: string;

  private getUrl(): string | null {
    if (this.name) {
      return this.scope.makePath(`icons/${this.name.toLowerCase()}.svg`);
    }

    if (this.url) {
      return this.url;
    }

    return null;
  }

  private async setIcon() {
    const url = this.getUrl();

    if (url == null) {
      // If we can't resolve a URL and an icon was previously set, remove it
      if (this.svg) this.svg = '';
      return;
    }

    try {
      const file = await requestIcon(url)!;

      if (url !== this.getUrl()) {
        // If the url has changed while fetching the icon, ignore this request.
        return;
      } else if (file.ok) {
        const doc = parser.parseFromString(file.svg, 'text/html');
        const svgEl = doc.body.querySelector('svg');

        if (svgEl) {
          this.svg = svgEl.outerHTML;
          this.emit('he-load');
        } else {
          this.svg = '';
          this.emit('he-error', { detail: { status: file.status } });
        }
      } else {
        this.svg = '';
        this.emit('he-error', { detail: { status: file.status } });
      }
    } catch {
      this.emit('he-error', { detail: { status: -1 } });
    }
  }

  /** @internal watcher */
  @watch('name')
  @watch('url')
  handleNameChange() {
    this.setIcon();
  }

  render() {
    return html`
      <span
        part="base"
        role=${ifDefined(this.label ? 'img' : undefined)}
        aria-label=${ifDefined(this.label || undefined)}
        aria-hidden=${this.label ? 'false' : 'true'}
      >
        ${unsafeSVG(this.svg)}
      </span>
    `;
  }
}

export default Icon;
